import { Campaign, LogService, Media, VisionService } from '@hub/shared'
import MetricBar from '@src/components/metric-bar/metric-bar.component'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SqCarousel, SqLoader } from '@squidit/react-css'
import './campaign-media-reach.component.scoped.scss'

interface Props {
  campaign: Campaign
  metricType?: string
  socialId?: string
}

export default ({ campaign, metricType, socialId }: Props) => {
  const [t] = useTranslation('reportPage')
  const visionService = useMemo(() => new VisionService(), [])
  const [media, setMedia] = useState<Media>()
  const [loading, setLoading] = useState(false)
  const logService = useMemo(() => new LogService(), [])

  const [isVideoError, setIsVideoError] = useState(false)

  const imageFallback = useCallback(() => {
    return <img src={media?.imagens?.resolucaoPadrao?.url} alt={media?.legenda} />
  }, [media?.imagens?.resolucaoPadrao?.url, media?.legenda])

  let startDate = useMemo(() => {
    return `${new Date(campaign?.activation?.startDate).toISOString().split('T')[0]}T00:00:00-03:00`
  }, [campaign?.activation?.startDate])

  let endDate = useMemo(() => {
    return campaign?.endDate
      ? `${new Date(campaign?.endDate).toISOString().split('T')[0]}}T23:59:59-03:00`
      : `${new Date().toISOString().split('T')[0]}T23:59:59-03:00`
  }, [campaign?.endDate])

  const timezone = useMemo(() => campaign?.timezone || 'America/Sao_Paulo', [campaign?.timezone])

  const onGetMediaReach = useCallback(async () => {
    if (!loading && metricType !== 'shareLinks' && !media) {
      setLoading(true)
      try {
        const response = await visionService.getCampaignUserMedias({
          socialId,
          campaignId: campaign?._id,
          socialNetwork: campaign?.socialNetworks[0] as 'instagram' | 'tiktok' | 'twitter' | 'youtube',
          perPage: 1,
          page: 0,
          sort_by: metricType,
          order_by: '-1',
          status: ['approved', 'none'],
          type: ['imagem', 'video', 'carousel', 'imagem_stories', 'video_stories'],
          start_date: startDate,
          end_date: endDate,
          timezone,
        })
        setMedia(response?.data?.[0])
      } catch (error) {
        logService.error({ message: 'Failed to get media', error })
      } finally {
        setLoading(false)
      }
    }
  }, [campaign, loading, metricType, socialId, visionService, logService, startDate, endDate, media, timezone])

  useEffect(() => {
    onGetMediaReach()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!media || metricType === 'shareLinks') {
    return null
  }

  if (loading) {
    return (
      <div className="display-flex justify-content-center">
        <SqLoader size="big" />
      </div>
    )
  }

  return (
    <div className="card-media-reach">
      <p className="mb-4">
        <i className="fa-solid fa-bullseye-arrow lg mr-2"></i>
        {t(`${metricType}.featuredPost`)}
      </p>
      <div className="mt-3">
        <div className="">
          {['imagem', 'imagem_stories'].includes(media?.tipo) && imageFallback()}
          {['video', 'video_stories'].includes(media?.tipo) &&
            (media?.videos?.resolucaoMedia?.url && !isVideoError ? (
              <div>
                <video controls onError={() => setIsVideoError(true)}>
                  <source src={media?.videos?.resolucaoMedia?.url} />
                </video>
              </div>
            ) : (
              imageFallback()
            ))}
          {media?.carousel?.length}
          {media?.tipo === 'carousel' ? (
            media?.carousel?.length ? (
              <SqCarousel
                showPagination
                key={media?._id}
                slides={media?.carousel?.map((slide) => (
                  <img src={slide?.url} alt={media?.legenda} />
                ))}
              />
            ) : (
              <img src={media?.imagens?.resolucaoMedia?.url} alt={media?.legenda} />
            )
          ) : null}
        </div>
        <div className="mt-3">
          <div className="post-description mb-3">
            <p className="description" key={1}>
              <span className="username text-bold">@{media?.usuario?.username}</span>
              {` ${media?.legenda}`}
            </p>
            <p key={2}>{!!media?.tags && media?.tags?.map((tag) => <span className="tag">{`#${tag}`}</span>)}</p>
          </div>
          <MetricBar media={media} />
        </div>
      </div>
    </div>
  )
}
